import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { makeStyles, Step, StepLabel, Stepper, useMediaQuery, Typography } from '@material-ui/core';
import { ArrowBackIos } from '@material-ui/icons';

import Time from './time/Time';
import Service from './service';
import Staff from './staff/Staff';
import Confirm from './confirm/Confirm';
import Location from './location/Location';
import LoginModal from '../common/LoginModal';
import BookingAppointmentDetails from './BookingAppointmentDetails';
import ClinicApi from '../../api/clinicApi';
import ServicesApi from '../../api/servicesApi';

import { MOBILE } from '../../constants/breakpoints';
import {
    changeStep,
    setCurrentCategory,
    setCurrentTimeStep,
    setCurrentConfirmStep,
    changeService,
    changeLocation,
    updateAppointments,
    chooseTime,
    setCurrentDate,
    updateGiftVouchers
} from '../../redux/actions/book-appointment';
import {
    getCurrentStep,
    getCurrentCategory,
    getCurrentTimeStep,
    getCurrentConfirmStep,
    getCurrentAppointments,
    getAppointmentIdToRemove,
    getCurrentGiftVouchers
} from '../../redux/selectors/book-appointment';
import { CONFIRM, LOCATION, SERVICE, STAFF, TIME, CREDIT_CARD } from '../../constants/steps';

import { stepStyles } from './styles';
import InfoModal from '../common/InfoModal';
import { changeModalVisibility } from '../../redux/actions/auth';
import { useLocation, useParams, useHistory, useRouteMatch } from 'react-router-dom';
import AppointmentApi from '../../api/AppointmentApi';
import CoursesApi from '../../api/coursesApi';

const steps = [LOCATION, SERVICE, STAFF, TIME, CONFIRM];

export default function BookAppointment() {
    const [countdown, setCountdown] = useState({
        minutes: 5,
        seconds: 0
    });
    const [countdownStarted, setCountdownStarted] = useState(null);

    const [openInfoModal, setOpenInfoModal] = useState(false);
    const [infoText, setInfoText] = useState('');
    const [clinics, setClinics] = useState([]);
    const [forceCourseView, setForceCourseView] = useState(false);
    const [stepContentMinHeight, setStepContentMinHeight] = useState(0);

    const { url } = useRouteMatch();

    const dispatch = useDispatch();
    // const availableSteps = useAvailableSteps();
    // const socket = useSocket();

    const isMobile = useMediaQuery(`(max-width:${MOBILE}px)`);
    const classes = makeStyles(theme => stepStyles(theme))();

    // The fallbackstep will get used once the user navigates past the currently routed steps
    // and stops relying on the route parameters.
    // We do this because not all routes are currently implemented (confirm, date/time, complete)
    // and want to retain original functionality for those steps.
    const fallbackStep = useSelector(getCurrentStep);
    const currentCategory = useSelector(getCurrentCategory);

    const history = useHistory();
    const { locationId, categoryId, serviceId } = useParams();
    const urlParams = new URLSearchParams(useLocation().search);
    const serviceIndex = urlParams.get('service') || 0;
    const isCourseBooking = urlParams.get('course') === 'true';
    const isCourseServiceBooking = urlParams.get('courseServiceBooking') === 'true';

    const buildUrlWithCourseParams = (baseUrl, additionalParams = '') => {
        const params = new URLSearchParams();

        if (additionalParams.includes('service=')) {
            params.set('service', serviceIndex);
        }

        if (additionalParams.includes('view=')) {
            const viewMatch = additionalParams.match(/view=([^&]*)/);
            if (viewMatch) params.set('view', viewMatch[1]);
        }

        if (isCourseBooking) params.set('course', 'true');
        if (isCourseServiceBooking) params.set('courseServiceBooking', 'true');

        const queryString = params.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    };

    const bannerHeight = document.querySelector('.MuiBox-root-21')?.height || 0;

    const currentAppointments = useSelector(getCurrentAppointments);
    const currentGiftVouchers = useSelector(getCurrentGiftVouchers);
    const hasCourses = currentAppointments.some(app => app?.service?.salePrice !== undefined);

    const currentAppointmentsRef = useRef(currentAppointments);
    currentAppointmentsRef.current = currentAppointments;

    const currentStep = useMemo(() => {
        if (isCourseBooking && serviceId && ![TIME, CONFIRM, CREDIT_CARD].includes(fallbackStep)) {
            return STAFF;
        }

        if (isCourseBooking && serviceId && [TIME, CONFIRM, CREDIT_CARD].includes(fallbackStep)) {
            return fallbackStep;
        }

        if (![LOCATION, STAFF].includes(fallbackStep)) {
            return fallbackStep;
        }

        if (serviceId) {
            const step = hasCourses ? CONFIRM : STAFF;
            return step;
        }
        if (categoryId || locationId) {
            return SERVICE;
        }

        return LOCATION;
    }, [locationId, serviceId, categoryId, fallbackStep, hasCourses, isCourseBooking]);

    const isBookAgain = useSelector(state => state.bookAppointment.isBookAgain);
    const isLinked = useSelector(state => state.bookAppointment.isLinked);
    const appointmentIdToRemove = useSelector(getAppointmentIdToRemove);
    const isReschedule = useSelector(state => state.bookAppointment.isReschedule);
    const hasLinked = useSelector(state => state.bookAppointment.linkedAppointments);
    const currentDate = useSelector(state => state.bookAppointment.currentDate);
    const modalVisibility = useSelector(state => state.auth.loginModalVisible);
    const renderCards = useSelector(getCurrentConfirmStep);
    const renderStepContent = {
        [LOCATION]: <Location />,
        [SERVICE]: <Service serviceIndex={serviceIndex} forceCourseView={forceCourseView} />,
        [STAFF]: <Staff serviceIndex={serviceIndex} />,
        [TIME]: <Time />,
        [CONFIRM]: <Confirm />,
        [CREDIT_CARD]: <Confirm />
    }[currentStep];

    useEffect(() => {
        if (hasCourses && (currentStep === STAFF || currentStep === TIME)) {
            dispatch(changeStep(CONFIRM));
        }
    }, [hasCourses, currentStep, dispatch]);

    useEffect(() => {
        setTimeout(() => {
            const stepElement = document.querySelector('.MuiStepper-root');
            if (stepElement) {
                const rect = stepElement.getBoundingClientRect();
                setStepContentMinHeight(window.innerHeight - rect.height);
            }
        }, 2000);
    }, []);

    useEffect(() => {
        async function getClinics() {
            const res = await ClinicApi.listPublicClinics();
            setClinics(res);

            let selectedClinic = res?.filter(c => c.id === locationId)[0];

            if (selectedClinic) {
                dispatch(changeLocation(selectedClinic));
            } else if (locationId) {
                if (res && res.length > 0) {
                    selectedClinic = res[0];
                    dispatch(changeLocation(selectedClinic));
                }
            }
        }

        getClinics();
    }, [locationId, dispatch, currentStep]);

    useEffect(() => {
        async function getCategory() {
            if (!categoryId) return;
            const selectedCategory = await ServicesApi.getCategory(categoryId);

            if (!selectedCategory) {
                const courseCategory = (await ServicesApi.getCoursesByCategory()).find(el => el.id === categoryId)
                    ?.category;
                if (courseCategory) {
                    setTimeout(() => {
                        setForceCourseView(true);
                        dispatch(setCurrentCategory(courseCategory));
                    }, 300);
                }
                return;
            }

            dispatch(setCurrentCategory(selectedCategory));
        }

        getCategory();
    }, [categoryId, dispatch]);

    useEffect(() => {
        async function getServices() {
            if (!serviceId) return;
            const selectedService = await ServicesApi.getService(serviceId);

            if (!selectedService) {
                const selectedCourse = await CoursesApi.getCourse(serviceId);
                if (!selectedCourse) {
                    return;
                }
                if (currentAppointmentsRef.current.length === 0) {
                    dispatch(updateAppointments([{ service: selectedCourse, staff: null }]));
                }
                return;
            }

            dispatch(setCurrentCategory(selectedService.category));

            const latestAppts = currentAppointmentsRef.current;
            const existingAppt = latestAppts[serviceIndex];

            if (existingAppt?.staff) {
                dispatch(changeService(selectedService));
                return;
            }

            const tempCurrAppts = [...latestAppts];
            tempCurrAppts[serviceIndex] = {
                service: selectedService,
                staff: null
            };

            if (!isReschedule) {
                dispatch(updateAppointments(tempCurrAppts));
            }
            dispatch(changeService(selectedService));
        }

        getServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceId, dispatch, isReschedule]);

    const subScreens = [
        {
            name: SERVICE,
            getter: useSelector(getCurrentCategory),
            setter: setCurrentCategory
        },
        {
            name: TIME,
            getter: useSelector(getCurrentTimeStep),
            setter: setCurrentTimeStep
        },
        {
            name: CONFIRM,
            getter: useSelector(getCurrentConfirmStep),
            setter: setCurrentConfirmStep
        }
    ];

    useEffect(() => {
        const isCourseOnly =
            currentAppointments.length > 0 && currentAppointments.every(app => app?.service?.salePrice);
        const isGiftVoucherOnly = !!currentGiftVouchers.length;

        if (
            !isBookAgain &&
            currentStep === CONFIRM &&
            renderCards === CREDIT_CARD &&
            !isCourseOnly &&
            !isGiftVoucherOnly
        ) {
            const timer = setTimeout(() => {
                const handleCountdown = async () => {
                    const seconds = 300;
                    const currentDate = new Date();
                    const diffInSeconds = Math.floor((currentDate - countdownStarted) / 1000);
                    const secondsLeft = Math.max(0, seconds - diffInSeconds);

                    if (countdown.seconds === 0 && countdown.minutes === 0) {
                        if (appointmentIdToRemove && !isReschedule) {
                            await AppointmentApi.deletePendingAppointments(appointmentIdToRemove);
                        }
                        window.location.href = '/app';
                        return;
                    }

                    const _countdown = {
                        minutes: Math.floor(secondsLeft / 60),
                        seconds: secondsLeft % 60
                    };

                    setCountdown(_countdown);
                };

                handleCountdown();
            }, 1000);

            if (!countdownStarted) {
                setCountdownStarted(new Date());
            }

            return () => clearTimeout(timer);
        }
    }, [
        isBookAgain,
        currentStep,
        countdown,
        appointmentIdToRemove,
        isReschedule,
        renderCards,
        countdownStarted,
        currentAppointments,
        currentGiftVouchers
    ]);

    const handleClickStep = step => {
        if (hasCourses && (step === STAFF || step === TIME)) {
            return;
        }

        if (step === STAFF) {
            const lastAppointment = currentAppointments[currentAppointments.length - 1];
            const tempCurrAppts = [...currentAppointments];
            dispatch(changeService(lastAppointment.service));
            tempCurrAppts.splice(currentAppointments.length - 1, 1, {
                ...tempCurrAppts[currentAppointments.length - 1],
                staff: null
            });
            dispatch(updateAppointments(tempCurrAppts));
            dispatch(changeStep(step));
            return;
        }
        if (step === SERVICE) {
            const tempCurrAppts = [...currentAppointments];
            dispatch(changeService(null));
            tempCurrAppts.splice(currentAppointments.length - 1, 1);
            dispatch(updateAppointments(tempCurrAppts));
            dispatch(changeStep(step));
            return;
        }
        dispatch(changeStep(step));
    };

    const backToLastScreen = () => {
        const hasSubScreens = subScreens.find(screen => currentStep === screen.name);
        if (currentStep === CONFIRM && currentGiftVouchers.length) {
            dispatch(updateGiftVouchers([]));
            dispatch(changeStep(SERVICE));
            const courseUrl = `/step/${locationId}`;
            history.replace(courseUrl);
            return;
        }

        setCountdown({ minutes: 5, seconds: 0 });
        const servicePointer = '?service=' + serviceIndex;
        if (hasSubScreens) {
            if (hasSubScreens.getter) {
                dispatch(hasSubScreens.setter(null));

                if (currentStep === 'time') {
                    dispatch(chooseTime(null));
                    dispatch(setCurrentDate(null));
                } else if (currentStep === 'service') {
                    if (currentCategory) {
                        if (currentCategory.parentCategory) {
                            dispatch(setCurrentCategory(currentCategory.parentCategoryData[0]));
                            const courseUrl = `/step/${locationId}/${currentCategory.parentCategory}${servicePointer}`;
                            history.replace(courseUrl);
                            return;
                        }
                        dispatch(setCurrentCategory(null));
                        const courseUrl = `/step/${locationId}${servicePointer}`;
                        history.replace(courseUrl);
                        return;
                    }

                    dispatch(changeService(null));
                    dispatch(setCurrentCategory(null));
                    dispatch(updateAppointments([]));
                    dispatch(changeLocation(null));
                    history.push('/');
                } else if (currentStep === CONFIRM) {
                    if (hasCourses && currentAppointments.length > 0) {
                        dispatch(changeService(null));
                        dispatch(setCurrentCategory(null));
                        const courseUrl = buildUrlWithCourseParams(
                            `/step/${locationId}`,
                            `${servicePointer}&view=courses`
                        );
                        history.replace(courseUrl);
                    } else {
                        const serviceUrl = buildUrlWithCourseParams(
                            `/step/${locationId}/${categoryId}/${serviceId}`,
                            servicePointer
                        );
                        history.push(serviceUrl);
                        const previousStep = hasCourses ? SERVICE : steps[steps.indexOf(currentStep) - 1];
                        handleClickStep(previousStep);
                    }
                }
            } else {
                if (currentStep === 'time') {
                    history.push(`/step/${locationId}${servicePointer}`);
                    dispatch(changeStep(LOCATION));
                    return;
                }

                if (currentStep === SERVICE && categoryId) {
                    if (currentCategory) {
                        dispatch(setCurrentCategory(null));
                        return;
                    }

                    dispatch(changeService(null));
                    dispatch(setCurrentCategory(null));
                    dispatch(updateAppointments([]));
                    dispatch(changeLocation(null));
                    history.push('/');
                    return;
                }

                if (currentStep === SERVICE) {
                    dispatch(changeService(null));
                    dispatch(setCurrentCategory(null));
                    dispatch(updateAppointments([]));
                    dispatch(changeLocation(null));
                    history.push('/');
                    dispatch(changeStep(LOCATION));
                    return;
                }

                if (currentStep === CONFIRM) {
                    if (hasCourses && currentAppointments.length > 0) {
                        dispatch(changeService(null));
                        dispatch(setCurrentCategory(null));

                        const courseUrl = `/step/${locationId}${servicePointer}&view=courses`;
                        history.replace(courseUrl);
                        dispatch(changeStep(SERVICE));
                    } else {
                        const serviceUrl = `/step/${locationId}/${categoryId}/${serviceId}${servicePointer}`;
                        history.push(serviceUrl);
                        const previousStep = hasCourses ? SERVICE : steps[steps.indexOf(currentStep) - 1];
                        handleClickStep(previousStep);
                    }
                    return;
                }
            }
        } else {
            if (![LOCATION, STAFF, SERVICE].includes(currentStep)) {
                const previousStep =
                    currentStep === CONFIRM && hasCourses ? SERVICE : steps[steps.indexOf(currentStep) - 1];
                handleClickStep(previousStep);
                return;
            }

            if (currentStep === STAFF) {
                const currentAppointment = currentAppointments[Number(serviceIndex)];
                const tempCurrAppts = [...currentAppointments];

                if (Number(serviceIndex) === currentAppointments.length - 1) {
                    tempCurrAppts.splice(Number(serviceIndex), 1);
                    dispatch(changeService(null));
                    dispatch(updateAppointments(tempCurrAppts));
                    const newServiceIndex = tempCurrAppts.length;
                    const navigationUrl = buildUrlWithCourseParams(
                        `/step/${locationId}/${categoryId}`,
                        `service=${newServiceIndex}`
                    );
                    history.push(navigationUrl);
                } else {
                    dispatch(changeService(currentAppointment.service));
                    tempCurrAppts.splice(Number(serviceIndex), 1, {
                        ...currentAppointment,
                        staff: null
                    });
                    dispatch(updateAppointments(tempCurrAppts));
                    history.push(buildUrlWithCourseParams(`/step/${locationId}/${categoryId}`, servicePointer));
                }
                return;
            }

            if (currentStep === SERVICE && categoryId) {
                if (currentCategory) {
                    dispatch(setCurrentCategory(null));
                    history.push(buildUrlWithCourseParams(`/step/${locationId}`, servicePointer));
                    return;
                }
                const tempCurrAppts = [...currentAppointments];
                dispatch(changeService(null));
                tempCurrAppts.splice(currentAppointments.length - 1, 1);
                dispatch(updateAppointments(tempCurrAppts));
                history.push(buildUrlWithCourseParams(`/step/${locationId}`, servicePointer));
                return;
            }

            handleClickStep(steps[steps.indexOf(currentStep) - 1]);
        }
    };

    const showBackButton = () => {
        if (currentStep === LOCATION) return false;

        if (currentStep === SERVICE) return true;

        if ([STAFF, TIME, CONFIRM].includes(currentStep)) return true;

        const urlParts = url.split('/');
        if (currentStep === SERVICE && urlParts.length === 3 && clinics.length === 1) return true;

        if (currentStep === TIME && (isLinked || (hasLinked && hasLinked.length)) && !currentDate) {
            return true;
        }

        return false;
    };

    return (
        <div style={{ minHeight: `${window.innerHeight + bannerHeight}px` }}>
            <div className={isMobile && currentStep === CONFIRM ? null : null} style={{ position: 'relative' }}>
                <Stepper
                    classes={{ root: classes.stepper }}
                    activeStep={steps.indexOf(currentStep)}
                    nonLinear={!hasCourses}
                    alternativeLabel={isMobile}
                >
                    <Step key={LOCATION} completed={steps.indexOf(currentStep) > 0}>
                        <StepLabel>Location</StepLabel>
                    </Step>
                    <Step key={SERVICE} completed={steps.indexOf(currentStep) > 1}>
                        <StepLabel>Services</StepLabel>
                    </Step>
                    <Step key={STAFF} disabled={hasCourses} completed={steps.indexOf(currentStep) > 2}>
                        <StepLabel>Staff</StepLabel>
                    </Step>
                    <Step key={TIME} disabled={hasCourses} completed={steps.indexOf(currentStep) > 3}>
                        <StepLabel>Time</StepLabel>
                    </Step>
                    <Step key={CONFIRM} completed={steps.indexOf(currentStep) > 4}>
                        <StepLabel>Confirm</StepLabel>
                    </Step>
                </Stepper>
                {currentStep === CONFIRM &&
                    !isBookAgain &&
                    renderCards === CREDIT_CARD &&
                    !!currentAppointments.length &&
                    !(currentAppointments.length > 0 && currentAppointments.every(app => app?.service?.salePrice)) && (
                        <div className={classes.countdownMessage}>
                            <Typography variant="body1" component="span">
                                Your appointment will be held for {countdown.minutes}:
                                {(countdown.seconds === 0 || countdown.seconds.toString().length < 2) && '0'}
                                {countdown.seconds}
                            </Typography>
                        </div>
                    )}
            </div>
            <div className={classes.stepContainer}>
                {showBackButton() ? (
                    <div className={classes.backButton} onClick={() => backToLastScreen()}>
                        <ArrowBackIos />
                        Back
                    </div>
                ) : (
                    <></>
                )}
                <div style={isMobile ? { minHeight: stepContentMinHeight } : {}}>
                    <div className={classes.stepContent}>
                        <div className={classes.stepContentMain}>{renderStepContent}</div>

                        {(!isMobile || currentStep === CONFIRM || (hasCourses && currentAppointments.length > 0)) && (
                            <div
                                className={
                                    [CONFIRM, STAFF, TIME].includes(currentStep)
                                        ? classes.previewContentNoNav
                                        : classes.previewContent
                                }
                            >
                                <BookingAppointmentDetails clinics={clinics} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {!!modalVisibility.visible && <LoginModal setOpenInfoModal={setOpenInfoModal} setInfoText={setInfoText} />}
            {openInfoModal && (
                <InfoModal
                    onClose={() => {
                        dispatch(changeModalVisibility({ visible: true }));
                        setOpenInfoModal(false);
                    }}
                    openInfoModal={openInfoModal}
                    infoText={infoText}
                />
            )}
        </div>
    );
}
